/**
 * The screen registry: every screen in the app, in nav order, with what people see about it. One list that both
 * halves read, with no React and no server code in it:
 *
 *   src/shared/screen-meta.ts   this file: path, title, description, icon, group, tab, draft
 *   src/screens.ts              adds each screen's component (the browser)
 *   src/server/routes.ts        adds each screen's data (the server)
 *
 * The nav, the tab titles, the command palette, GET /api/screens (which cpg.computer reads to offer sharing) and,
 * later, the app's MCP tools all come from here. `bun run new:screen` adds an entry to all three files.
 *
 * - path: the URL, lowercase, one segment, matching the folder in src/screens. Never rename one once it's shared:
 *   grants point at it.
 * - title: the nav label and the page title. description: one plain sentence; the founder sees it when sharing.
 * - icon: a name from src/ui/primitives/Icon.tsx. group: a sidebar heading, once there are enough screens to need
 *   them. tab: pinned to the phone's bottom bar (up to four).
 * - draft: builders see it, viewers never do, and it isn't offered for sharing. New screens start as drafts; the ship
 *   skill asks the founder when one is ready.
 */
import type { IconName } from '../ui/primitives/Icon'

export interface ScreenMetaEntry {
  title: string
  description: string
  icon: IconName
  group?: string
  tab?: boolean
  draft?: boolean
}

export const screenMeta = {
  '/today': {
    title: 'Today',
    description: 'Orders and revenue so far today against the same day last week, top products and low stock.',
    icon: 'today',
    tab: true,
  },
  '/board': {
    title: 'Board report',
    description: 'Totals for a board update by month or quarter: revenue, orders, average order and weeks of stock against the prior period, with no customer details.',
    icon: 'chart',
  },
  // new screens go above this line (bun run new:screen)
} as const satisfies Record<`/${string}`, ScreenMetaEntry>

export type ScreenPath = keyof typeof screenMeta

/** Every screen path, in nav order. */
export const screenPaths = Object.keys(screenMeta) as ScreenPath[]

/** The entry for a path, if it's a screen. */
export function metaFor(path: string): ScreenMetaEntry | undefined {
  return (screenMeta as Record<string, ScreenMetaEntry>)[path]
}
